import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { getMemberAccessDoors, saveMemberAccessDoors } from '../../../../api/member-access-door';
import { listBranchDoors } from '../../../../api/branch-door';
import DoorSelectModal from '../../../common/modal/DoorSelectModal';
import { InfoRow, InfoLeft, InfoLabel, InfoValue } from './CourseTicketCardStyled';

// 회원 보유 권별 출입문 (#91 후속). 전환 지점(출입문 체계)에서만 데이터가 오고, 제한 ON 문만 추가/제거한다.
// 미제한 문은 공용 그룹으로 모든 상품이 항상 입장 가능하다.
// 지점 전환 여부를 먼저 1회 확인해 미전환 지점(대부분)에서 보유 권별 GET 폭주를 막는다
// (코드리뷰 CONFIRMED). 상품 폼과 같은 캐시 키를 공유하고 길게 캐시한다.
export const useMemberAccessDoors = (holdingType, holdingId) => {
  const { user } = useSelector(state => state.user);
  const token = user?.token;
  const branchId = user?.branch?.branchId;

  const convertedQuery = useQuery({
    queryKey: ['branch-doors-for-product', branchId],
    queryFn: () => listBranchDoors(branchId, token),
    enabled: !!branchId && !!token,
    staleTime: 5 * 60 * 1000,
  });
  const branchConverted = convertedQuery.data?.converted === true;

  // 다지점 공유 상품(#73)은 판매지점이 아니라 "지금 보고 있는 지점"의 문을 다룬다 — branchId 를 넘긴다.
  const queryKey = ['member-access-doors', holdingType, holdingId, branchId];
  const doorsQuery = useQuery({
    queryKey,
    queryFn: () => getMemberAccessDoors(holdingType, holdingId, token, branchId),
    enabled: branchConverted && !!holdingId && !!token,
  });

  const data = doorsQuery.data;
  const available = !!data?.converted && Array.isArray(data.doors) && data.doors.length > 0;
  return { token, branchId, queryKey, data, available };
};

// 카드 정보 영역의 출입문 행 — 표시 전용. 설정은 카드 하단 버튼(AccessDoorButton)에서 한다.
const AccessDoorRow = ({ holdingType, holdingId, isStopped }) => {
  const { data, available } = useMemberAccessDoors(holdingType, holdingId);
  if (!available) return null;

  const effectiveNames = data.doors.filter(d => d.effective).map(d => d.doorName);

  return (
    <InfoRow>
      <InfoLeft>
        <InfoLabel isStopped={isStopped}>출입문</InfoLabel>
        <InfoValue isStopped={isStopped}>
          {effectiveNames.length > 0 ? effectiveNames.join(', ') : '공용 출입문만'}
        </InfoValue>
      </InfoLeft>
    </InfoRow>
  );
};

// 상품 정보 수정 모달의 "출입문" 탭 본문. 전환 지점이 아니면 available=false 라 탭 자체가 안 생긴다.
export const useAccessDoorAvailable = (holdingType, holdingId) =>
  useMemberAccessDoors(holdingType, holdingId).available;

export const AccessDoorPanel = ({ holdingType, holdingId, onDone }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { token, branchId, queryKey, data, available } = useMemberAccessDoors(holdingType, holdingId);
  const [selectedIds, setSelectedIds] = useState(null); // null = 아직 서버값으로 초기화 전

  const saveMutation = useMutation({
    mutationFn: groupIds => saveMemberAccessDoors(holdingType, holdingId, groupIds, token, branchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('이 회원권의 출입문 설정을 저장했습니다.');
      onDone?.();
    },
    onError: e => toast.error(e?.message ?? '출입문 설정 저장에 실패했습니다.'),
  });

  if (!available) return null;

  const initial = data.doors.filter(d => d.restricted && d.effective).map(d => d.groupId);
  const current = selectedIds ?? initial;
  const modalDoors = data.doors.map(d => ({
    id: d.groupId,
    doorName: d.doorName,
    restricted: d.restricted,
    badge: d.restricted && d.fromProduct ? '상품 기본' : null,
  }));

  return (
    <DoorSelectModal
      embedded
      open
      subtitle="이 회원권에만 적용됩니다. 출입 제한이 없는 문은 항상 입장 가능해 체크가 고정됩니다."
      doors={modalDoors}
      selectedIds={current}
      onChangeIds={setSelectedIds}
      onClose={() => onDone?.()}
      onConfirm={() => saveMutation.mutate(current)}
      confirmLabel={saveMutation.isPending ? '저장 중...' : '저장'}
      confirming={saveMutation.isPending}
    />
  );
};

export default AccessDoorRow;
